import { theme } from '@arnold/common';
import styled from '@emotion/styled/macro';
import { Card } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { ReportSequenceSubscriptionSubscription } from '../../generated/hooks';
import { calculatePercentage } from './PhasesSection';

type ReportSequenceData = NonNullable<NonNullable<ReportSequenceSubscriptionSubscription>['getProcessReport']>;

interface IProps {
  reportSequence: ReportSequenceData;
  getTopicUrl: (topicId: string) => string;
}

export const PhasesSectionMobile = ({ reportSequence, getTopicUrl }: IProps) => {
  const history = useHistory();
  const { t, i18n } = useTranslation(['Report', 'ReportSequence']);

  const anyMetricQuestions = reportSequence.surveyGroup?.steps.some((step) => step.hasMetricQuestion);
  const anyStepHasMetricAbove7 = reportSequence.surveyGroup?.steps.some(
    (step) => step.statistics?.avgMetric && step.statistics?.avgMetric > 7,
  );

  const feelingsScale = anyStepHasMetricAbove7 ? 10 : 7;

  const tableData = getTableData(reportSequence, i18n.language);
  return (
    <>
      <PhasesCard>
        <Container data-cy="report-onb-phases-mobile-table">
          <HeaderRow>
            <PhaseTitleCell>{t('topics')}</PhaseTitleCell>
            <RespondedCell>{t('RESPONSE_RATE')}</RespondedCell>
            {anyMetricQuestions && <FeelingsCell>{t('phasesFeeling')}*</FeelingsCell>}
          </HeaderRow>
          {tableData.map((data) => (
            <Row key={data.id}>
              <PhaseTitleDataCell onClick={() => history.push(getTopicUrl(data.id))}>
                {data.stepTitle}
              </PhaseTitleDataCell>
              <RespondedCell>
                {data.answeredPercentage}% ({data.answered}/{data.total})
              </RespondedCell>
              {anyMetricQuestions && (
                <FeelingsCell>
                  {data.avgMetric !== 0 && (
                    <FeelingsPill background={data.avgMetricColor.background} color={data.avgMetricColor.color}>
                      {Math.round(data.avgMetric)}
                    </FeelingsPill>
                  )}
                </FeelingsCell>
              )}
            </Row>
          ))}
        </Container>
        <Note>
          <Trans i18nKey="Report:phasesFeelingNote" components={{ bold: <strong /> }} values={{ max: feelingsScale }} />
        </Note>
      </PhasesCard>
    </>
  );
};

const getTableData = (reportSequence: ReportSequenceData, userLanguage: string) => {
  return reportSequence.surveyGroup!.steps.map((step, i) => {
    const stepTitle =
      step.processStep.stepTopicGroup.translations?.find((trans) => userLanguage === trans.language.code)?.value ||
      step.processStep.stepTopicGroup.translations![0].value;
    const stats = step.statistics!;
    const answered = stats.answered;
    const total = stats.total;
    const answeredPercentage = calculatePercentage(answered, total);
    const avgMetric = stats.avgMetric;
    const avgMetricColor = getAvgMetricColor(avgMetric);
    return {
      stepTitle,
      total,
      answered,
      answeredPercentage,
      avgMetric,
      avgMetricColor,
      step: i,
      id: step.processStep.stepTopicGroup.id,
    };
  });
};

const getAvgMetricColor = (value: number) => {
  if (value >= 5 || value === 0.1) {
    return {
      color: theme.colors.emotionSuccess.hover,
      background: theme.colors.emotionSuccess.backgroundActive,
    };
  }
  if (value < 4) {
    return {
      color: theme.colors.emotionDanger.hover,
      background: theme.colors.emotionDanger.backgroundActive,
    };
  }
  return {
    color: theme.colors.emotionWarning.hover,
    background: theme.colors.emotionWarning.backgroundActive,
  };
};

const PhasesCard = styled(Card)`
  margin-top: ${theme.spacing.g};
  padding: ${theme.spacing.f} ${theme.spacing.f};
`;

const Container = styled.div`
  width: 100%;
  display: inline-flex;
  flex-direction: column;
  border-bottom: 1px solid ${theme.colors.borderSeparator.default};
`;

const Row = styled.div`
  min-height: 24px;
  margin: 10px 0;
  color: ${theme.colors.text.primary};
  display: inline-flex;
  padding: ${theme.spacing.d};
  font-size: 12px;
  justify-content: space-between;
  align-items: center;
`;

const HeaderRow = styled(Row)`
  margin-top: 0px;
  font-size: 11px;
  color: ${theme.colors.text.secondary};
  background-color: ${theme.colors.backgroundBasic.default};
  border-radius: ${theme.borderRadius.l};
  align-items: flex-start;
`;

const PhaseTitleCell = styled.div`
  flex-grow: 1;
`;

const RespondedCell = styled.div`
  width: 92px;
  padding-left: ${theme.spacing.b};
  padding-right: ${theme.spacing.b};
`;

const FeelingsCell = styled.div`
  width: 65px;
  margin-right: ${theme.spacing.d};
`;

const PhaseTitleDataCell = styled(PhaseTitleCell)`
  width: min-content;
  font-weight: 500;
  color: ${theme.colors.actionPrimary.default};
  padding-right: ${theme.spacing.d};
`;

const FeelingsPill = styled.span<{ color: string; background: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: ${theme.spacing.c};
  width: 24px;
  height: 24px;
  border-radius: 50%;
  color: ${(p) => p.color};
  background: ${(p) => p.background};
`;

const Note = styled.div`
  font-size: 12px;
  color: ${theme.colors.text.secondary};
  margin-top: ${theme.spacing.f};
`;
